import {
  BarChart3,
  BookOpen,
  Database,
  FileText,
  Globe,
  Layers,
  Link2,
  MessageSquare,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import React from "react";
import { Badge, Card, EmptyState, PageLayout } from "../components";
import { ContentAnalyticsSkeleton } from "../components/skeletons";
import { useTranslation } from "../contexts/LanguageContext";
import { useContentLibrary } from "../hooks/useContentLibrary";
import { cn } from "../utils/cn";

const ContentAnalytics: React.FC = () => {
  const { t } = useTranslation();
  const { contentLibrary, loading } = useContentLibrary({ loadAll: true });

  // Calcular estatísticas relevantes para biblioteca de conteúdo
  const totalContents = contentLibrary.length;

  // Distribuição por tipo
  const contentsByType = {
    url: contentLibrary.filter((c) => c.type === "url").length,
    text: contentLibrary.filter((c) => c.type === "text").length,
    prompt: contentLibrary.filter((c) => c.type === "prompt").length,
    file: contentLibrary.filter((c) => c.type === "file").length,
  };

  // Distribuição por business_type
  const contentsByBusinessType = {
    b2b: contentLibrary.filter((c) => c.business_type === "b2b").length,
    b2c: contentLibrary.filter((c) => c.business_type === "b2c").length,
    local: contentLibrary.filter((c) => c.business_type === "local").length,
  };

  // Conteúdos recentes (últimos 7 dias)
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const isRecent = (createdAt: string) => new Date(createdAt) >= sevenDaysAgo;
  const recentContents = contentLibrary.filter((c) =>
    isRecent(c.created_at),
  ).length;

  // Novos (7d) por business_type — usado para dar mais substância aos cards
  // de "Orientação de Mercado" sem inventar dado: é o mesmo cálculo de
  // recentContents, só filtrado por segmento.
  const recentByBusinessType = {
    b2b: contentLibrary.filter(
      (c) => c.business_type === "b2b" && isRecent(c.created_at),
    ).length,
    b2c: contentLibrary.filter(
      (c) => c.business_type === "b2c" && isRecent(c.created_at),
    ).length,
    local: contentLibrary.filter(
      (c) => c.business_type === "local" && isRecent(c.created_at),
    ).length,
  };

  // Novos (7d) por tipo de conteúdo — mesma lógica para a lista de
  // "Distribuição por Categoria" na coluna direita.
  const recentByType = {
    url: contentLibrary.filter(
      (c) => c.type === "url" && isRecent(c.created_at),
    ).length,
    text: contentLibrary.filter(
      (c) => c.type === "text" && isRecent(c.created_at),
    ).length,
    prompt: contentLibrary.filter(
      (c) => c.type === "prompt" && isRecent(c.created_at),
    ).length,
    file: contentLibrary.filter(
      (c) => c.type === "file" && isRecent(c.created_at),
    ).length,
  };

  return (
    <PageLayout
      title={t("Analytics de Biblioteca")}
      subtitle={t(
        "Métricas e insights inteligentes da sua base de conhecimento.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2! py-2!"
      loading={loading}
      skeleton={<ContentAnalyticsSkeleton />}
      actions={<></>}
    >
      {totalContents === 0 ? (
        <EmptyState
          icon={<BookOpen className="h-12 w-12 text-gray-400" />}
          title={t("Nenhum conteúdo encontrado")}
          description={t(
            "Adicione conteúdos à sua biblioteca para ver métricas e análises aqui.",
          )}
        />
      ) : (
        <div className="w-full flex flex-col gap-2">
          {/* Grid Principal: Composição 8/4 Otimizada em Densidade e Presença Visual.
              Sem altura forçada ao viewport (era lg:h-[calc(100vh-170px)]) — as
              colunas agora se igualam (items-stretch) ao conteúdo real mais alto
              entre as duas, não a um valor arbitrário de tela cheia. */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-stretch">
            {/* Coluna Esquerda: Banner + Orientação de Mercado */}
            <div className="lg:col-span-8 flex flex-col gap-2 h-full">
              {/* Insights da Base de Conhecimento (Social Intelligence) */}
              <Card
                variant="glass"
                size="sm"
                className="relative group overflow-hidden bg-linear-to-r from-purple-50/50 via-indigo-50/50 to-blue-50/50 border-purple-200! shadow-xs [&>div]:flex [&>div]:flex-col [&>div]:gap-3 w-full shrink-0"
              >
                {/* Efeito decorativo sutil no hover */}
                <div className="absolute inset-0 bg-linear-to-r from-purple-600/5 via-indigo-600/5 to-blue-600/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />

                <div className="relative z-10">
                  {/* Header do Banner */}
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center text-purple-600 shadow-md border border-purple-50 shrink-0">
                        <Sparkles className="h-5 w-5 animate-pulse" />
                      </div>
                      <div>
                        <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-purple-500 mb-0.5">
                          Social Intelligence
                        </h3>
                        <h2 className="text-lg font-bold text-gray-900 tracking-tight leading-none">
                          {t("Insights da Base de Conhecimento")}
                        </h2>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 leading-relaxed mb-3">
                    {t(
                      "Nossa IA analisa sua biblioteca para identificar padrões de sucesso e lacunas informativas na sua estratégia de conteúdo.",
                    )}
                  </p>

                  {/* Cards de Insights de IA com Bordas Coesas */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="bg-white/75 backdrop-blur-xs rounded-xl p-3 border border-purple-200 flex gap-2.5 items-start">
                      <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <BarChart3 className="h-3.5 w-3.5" />
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {t(
                          "Os insights analíticos serão gerados automaticamente conforme novos materiais são incorporados à sua biblioteca.",
                        )}
                      </p>
                    </div>

                    <div className="bg-white/75 backdrop-blur-xs rounded-xl p-3 border border-purple-200 flex gap-2.5 items-start">
                      <div className="w-6 h-6 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                        <Sparkles className="h-3.5 w-3.5" />
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {t("Identificamos que")}{" "}
                        <strong>
                          {contentsByBusinessType.b2b} {t("materiais")}
                        </strong>{" "}
                        {t(
                          "possuem foco B2B, sugerindo uma base sólida para expansão Enterprise.",
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Orientação de Mercado */}
              <Card
                variant="default"
                size="sm"
                className="border-purple-200! flex-1 [&>div]:h-full [&>div]:flex [&>div]:flex-col [&>div]:justify-between"
              >
                <div className="flex flex-col h-full justify-between">
                  {/* Header de Seção Compacto e Presente */}
                  <div className="flex items-center gap-2.5 border-b border-purple-100 pb-2 mb-3.5 shrink-0">
                    <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center text-emerald-600 border border-emerald-100 shadow-xs">
                      <Globe className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">
                        {t("Filtro Estratégico")}
                      </h3>
                      <h2 className="text-base font-bold text-gray-900 leading-tight">
                        {t("Orientação de Mercado")}
                      </h2>
                    </div>
                  </div>

                  {/* Grid de Segmentos */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 flex-1 items-stretch">
                    {[
                      {
                        title: "B2B Enterprise",
                        value: contentsByBusinessType.b2b,
                        recent: recentByBusinessType.b2b,
                        color: "blue",
                        desc: t("Vendas Corporativas"),
                      },
                      {
                        title: "B2C Direct",
                        value: contentsByBusinessType.b2c,
                        recent: recentByBusinessType.b2c,
                        color: "emerald",
                        desc: t("Consumidor Final"),
                      },
                      {
                        title: "Local Business",
                        value: contentsByBusinessType.local,
                        recent: recentByBusinessType.local,
                        color: "purple",
                        desc: t("Atuação Geográfica"),
                      },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className="relative group overflow-hidden rounded-xl border border-purple-200 bg-purple-50/10 p-3.5 hover:shadow-md transition-all flex flex-col justify-between h-full min-h-[120px]"
                      >
                        <div
                          className={cn(
                            "absolute top-0 right-0 w-16 h-16 blur-xl opacity-10 transition-opacity group-hover:opacity-20",
                            item.color === "blue"
                              ? "bg-blue-500"
                              : item.color === "emerald"
                                ? "bg-emerald-500"
                                : "bg-purple-500",
                          )}
                        />

                        <div className="relative flex-1 flex flex-col">
                          <div className="flex items-center justify-between">
                            <div
                              className={cn(
                                "px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border",
                                item.color === "blue"
                                  ? "bg-blue-50 text-blue-700 border-blue-100"
                                  : item.color === "emerald"
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                    : "bg-purple-50 text-purple-700 border-purple-200",
                              )}
                            >
                              {item.title}
                            </div>
                            <span className="text-[10px] font-bold text-gray-300 italic">
                              0{idx + 1}
                            </span>
                          </div>

                          <div className="mt-3">
                            <div className="text-2xl font-black text-gray-900 tracking-tighter leading-none">
                              {item.value}
                            </div>
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1">
                              {item.desc}
                            </div>
                          </div>

                          {/* Mini-métricas: % do total e novos (7d) do segmento */}
                          <div className="mt-3 grid grid-cols-2 gap-1.5">
                            <div className="rounded-lg bg-white/70 border border-purple-100 px-2 py-1.5">
                              <div className="text-sm font-black text-gray-800 leading-none">
                                {totalContents > 0
                                  ? `${Math.round((item.value / totalContents) * 100)}%`
                                  : "0%"}
                              </div>
                              <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wider mt-0.5">
                                {t("Do total")}
                              </div>
                            </div>
                            <div className="rounded-lg bg-white/70 border border-purple-100 px-2 py-1.5">
                              <div className="text-sm font-black text-gray-800 leading-none">
                                +{item.recent}
                              </div>
                              <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wider mt-0.5">
                                {t("Novos (7d)")}
                              </div>
                            </div>
                          </div>

                          {/* Barra de progresso ancorada no rodapé: absorve o espaço
                              sobrando da altura esticada (items-stretch com a coluna
                              direita) num único ponto, em vez de espalhá-lo entre os
                              3 blocos via justify-between. */}
                          <div className="mt-auto pt-3">
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <div
                                  key={i}
                                  className={cn(
                                    "h-1.5 flex-1 rounded-full transition-all duration-700",
                                    i <
                                      Math.round(
                                        (item.value / (totalContents || 1)) * 5,
                                      )
                                      ? item.color === "blue"
                                        ? "bg-blue-500"
                                        : item.color === "emerald"
                                          ? "bg-emerald-500"
                                          : "bg-purple-500"
                                      : "bg-gray-100",
                                  )}
                                  style={{ transitionDelay: `${i * 50}ms` }}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>

            {/* Coluna Direita: Painel de Ativos da Biblioteca */}
            <div className="lg:col-span-4 h-full">
              <Card
                variant="default"
                size="sm"
                className="border-purple-200! h-full [&>div]:h-full [&>div]:flex [&>div]:flex-col [&>div]:justify-between [&>div]:gap-4.5"
              >
                {/* Header de Ativos */}
                <div className="flex items-center justify-between border-b border-purple-100 pb-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600 border border-purple-200 shadow-xs">
                      <Layers className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">
                        {t("Dimensões da Base")}
                      </h3>
                      <h2 className="text-base font-bold text-gray-900 leading-tight">
                        {t("Ativos da Biblioteca")}
                      </h2>
                    </div>
                  </div>
                  <Badge
                    variant="purple"
                    size="sm"
                    className="font-bold uppercase tracking-wider px-1.5 text-[8px]"
                  >
                    {t("Monitoramento Ativo")}
                  </Badge>
                </div>

                {/* Mini KPIs de Volume */}
                <div className="grid grid-cols-2 gap-2 pb-2 border-b border-purple-100">
                  <div className="bg-blue-50/20 border border-purple-200 rounded-lg p-3 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                      <Database className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-[9px] font-bold uppercase tracking-wider text-gray-400">
                        {t("Total")}
                      </div>
                      <div className="text-xl font-black text-gray-900 leading-none mt-0.5">
                        {totalContents}
                      </div>
                    </div>
                  </div>

                  <div className="bg-emerald-50/20 border border-purple-200 rounded-lg p-3 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                      <TrendingUp className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-[9px] font-bold uppercase tracking-wider text-gray-400">
                        {t("Novos (7d)")}
                      </div>
                      <div className="text-xl font-black text-gray-900 leading-none mt-0.5">
                        {recentContents}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Categorias de Conteúdo */}
                <div className="space-y-2 flex-1 flex flex-col justify-between">
                  <h4 className="text-[9px] font-bold uppercase tracking-widest text-gray-400">
                    {t("Distribuição por Categoria")}
                  </h4>

                  <div className="space-y-3 flex-1 flex flex-col justify-between">
                    {[
                      {
                        title: t("Links Úteis"),
                        value: contentsByType.url,
                        recent: recentByType.url,
                        icon: Link2,
                        color: "blue",
                      },
                      {
                        title: t("Textos"),
                        value: contentsByType.text,
                        recent: recentByType.text,
                        icon: FileText,
                        color: "green",
                      },
                      {
                        title: t("IA Prompts"),
                        value: contentsByType.prompt,
                        recent: recentByType.prompt,
                        icon: MessageSquare,
                        color: "purple",
                      },
                      {
                        title: t("Arquivos"),
                        value: contentsByType.file,
                        recent: recentByType.file,
                        icon: FileText,
                        color: "orange",
                      },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className="bg-purple-50/5 border border-purple-200 rounded-xl p-2.5 hover:bg-white transition-all group relative overflow-hidden flex flex-col justify-between"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <div
                              className={cn(
                                "w-7 h-7 rounded-md flex items-center justify-center shrink-0",
                                item.color === "blue"
                                  ? "bg-blue-50 text-blue-600"
                                  : item.color === "green"
                                    ? "bg-emerald-50 text-emerald-600"
                                    : item.color === "purple"
                                      ? "bg-purple-50 text-purple-600"
                                      : "bg-orange-50 text-orange-600",
                              )}
                            >
                              <item.icon className="h-3.5 w-3.5" />
                            </div>
                            <span className="text-sm font-bold text-gray-700">
                              {item.title}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {item.recent > 0 && (
                              <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-full px-1.5 py-0.5">
                                +{item.recent} {t("7d")}
                              </span>
                            )}
                            <span className="text-sm font-black text-gray-900">
                              {item.value}
                            </span>
                          </div>
                        </div>

                        {/* H Progress Bar */}
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={cn(
                                "h-full rounded-full transition-all duration-700",
                                item.color === "blue"
                                  ? "bg-blue-500"
                                  : item.color === "green"
                                    ? "bg-emerald-500"
                                    : item.color === "purple"
                                      ? "bg-purple-500"
                                      : "bg-orange-500",
                              )}
                              style={{
                                width:
                                  totalContents > 0
                                    ? `${(item.value / totalContents) * 100}%`
                                    : "0%",
                              }}
                            />
                          </div>
                          <span className="text-[10px] font-bold text-gray-400 min-w-[20px] text-right">
                            {totalContents > 0
                              ? `${((item.value / totalContents) * 100).toFixed(0)}%`
                              : "0%"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ContentAnalytics;
